/**
 * The YouTube library, as queries.
 *
 * Everything the browsing panel shows arrives through here, and TanStack
 * Query owns all of it: the caching, the deduplication, the retry policy and
 * — the reason the search code got shorter — which answer belongs on screen.
 * A query is keyed by what was asked for, so a slow search landing after a
 * newer one lands in its own cache entry instead of in the panel. The
 * hand-rolled sequence guard and the client's own response map both went away
 * with it.
 *
 * The functions below return options, not results: a component wraps one in
 * `useQuery(() => …)` so that re-reading it when the account or the search
 * term changes switches the query rather than re-firing a fetch.
 */

import { type QueryClient, queryOptions } from '@tanstack/solid-query';
import { demoPlaylistTracks, demoSearch } from '../data/demo-library.ts';
import type { Unsubscribe } from '../lib/emitter.ts';
import { canSearchRemote, lib, refreshAccount } from '../state/library.ts';
import { toast } from '../state/ui.ts';
import type { Playlist, Track } from '../types/media.ts';
import { messageOf } from './playback.ts';

/** One prefix over everything the account can see, so signing out clears it. */
const ROOT = 'youtube';

export const libraryKeys = {
  all: [ROOT] as const,
  playlists: () => [ROOT, 'playlists'] as const,
  /* Liked music is keyed by its marker: its id is synthetic, not a real one. */
  tracks: (p: Playlist) => [ROOT, 'tracks', p.source, p.special ?? p.id] as const,
  /* Credentials change what a search can reach, so they are part of the key. */
  search: (term: string, remote: boolean) =>
    [ROOT, 'search', remote ? 'remote' : 'demo', term] as const,
};

/**
 * The account's own playlists.
 *
 * Deliberately not gated on being signed in: a read while signed out fails
 * with the client's own "sign in, or add an API key" message, which is
 * something the panel can show. A disabled query would only hang.
 */
export const playlistsQuery = () =>
  queryOptions({
    queryKey: libraryKeys.playlists(),
    queryFn: () => lib.myPlaylists(),
  });

/** The records inside a playlist, wherever the playlist came from. */
export function playlistTracksQuery(playlist: Playlist) {
  if (playlist.source === 'demo') {
    return queryOptions({
      queryKey: libraryKeys.tracks(playlist),
      queryFn: (): Track[] => demoPlaylistTracks(playlist.id),
      /* Generated in memory: there is nothing for it to go stale against. */
      staleTime: Number.POSITIVE_INFINITY,
    });
  }
  return queryOptions({
    queryKey: libraryKeys.tracks(playlist),
    queryFn: () =>
      playlist.special === 'liked' ? lib.likedTracks() : lib.playlistTracks(playlist.id),
  });
}

/**
 * A search, against YouTube or against the demo crate.
 *
 * Which of the two is part of the key rather than a branch inside the fetch,
 * so connecting an account does not leave demo results cached under a term
 * the real library could now answer.
 */
export function searchTracksQuery(term: string) {
  const remote = canSearchRemote();
  return queryOptions({
    queryKey: libraryKeys.search(term, remote),
    queryFn: (): Track[] | Promise<Track[]> => (remote ? lib.search(term) : demoSearch(term)),
    staleTime: remote ? undefined : Number.POSITIVE_INFINITY,
  });
}

/**
 * Ties the cache to the account, and failures to the toasts.
 *
 * Signing in or out changes who every cached answer belongs to, so the whole
 * prefix goes; the panel on screen refetches itself. The error handler is
 * here rather than on each call site because a quota message or a disabled
 * API is worth saying out loud wherever it was hit — the panels show their
 * own fallback, which cannot carry the detail.
 */
export function wireLibraryCache(client: QueryClient): Unsubscribe {
  const drop = (): void => {
    refreshAccount();
    client.removeQueries({ queryKey: libraryKeys.all });
  };

  const offSignIn = lib.on('signin', drop);
  const offSignOut = lib.on('signout', drop);
  const offErrors = client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'error') return;
    toast(messageOf(event.action.error, 'Could not reach YouTube.'));
  });

  return () => {
    offSignIn();
    offSignOut();
    offErrors();
  };
}
